export const MAX_OTHER_BUSINESS_TYPE_LENGTH = 120;

export type BusinessClassificationAnalyticsSource = 'registration' | 'dashboard';

export interface BusinessTypeOption {
  description: string | null;
  icon: string | null;
  id: string;
  name: string;
  requiresCustomText: boolean;
  searchAliases: string[];
  slug: string;
}

export interface BusinessCategoryOption {
  description: string | null;
  icon: string | null;
  id: string;
  name: string;
  searchAliases: string[];
  slug: string;
  types: BusinessTypeOption[];
}

export interface BusinessClassificationSelection {
  categoryId: string;
  categoryName: string;
  categorySlug: string;
  otherBusinessTypeText: string;
  requiresCustomText: boolean;
  typeId: string;
  typeName: string;
  typeSlug: string;
}

export interface BusinessCategoryAssignmentRecord {
  assignmentId: string;
  businessId: string;
  createdAt: string;
  hiddenByPlan: boolean;
  isActive: boolean;
  isPrimary: boolean;
  otherBusinessTypeText: string | null;
  typeId: string;
}

export interface BusinessClassificationAssignment
  extends BusinessClassificationSelection {
  assignmentId: string;
  businessId: string;
  createdAt: string;
  hiddenByPlan: boolean;
  isActive: boolean;
  isPrimary: boolean;
}

export interface BusinessTypeSearchResult {
  category: BusinessCategoryOption;
  type: BusinessTypeOption;
}

export function normalizeBusinessTaxonomyText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('es')
    .replace(/\s+/g, ' ')
    .trim();
}

function getTermScore(term: string, query: string): number {
  if (!term) return Number.POSITIVE_INFINITY;
  if (term === query) return 0;
  if (term.startsWith(query)) return 1;
  if (term.split(/\s+/).some((word) => word.startsWith(query))) return 2;
  if (term.includes(query)) return 3;
  return Number.POSITIVE_INFINITY;
}

function getBestScore(values: string[], query: string): number {
  return values.reduce(
    (bestScore, value) =>
      Math.min(bestScore, getTermScore(normalizeBusinessTaxonomyText(value), query)),
    Number.POSITIVE_INFINITY
  );
}

export function searchBusinessTaxonomy(
  categories: readonly BusinessCategoryOption[],
  query: string
): BusinessTypeSearchResult[] {
  const normalizedQuery = normalizeBusinessTaxonomyText(query);
  if (!normalizedQuery) return [];

  return categories
    .flatMap((category) => {
      const categoryScore = getBestScore(
        [category.name, ...category.searchAliases],
        normalizedQuery
      );

      return category.types.flatMap((type) => {
        const typeScore = getBestScore(
          [type.name, ...type.searchAliases],
          normalizedQuery
        );
        const score = Math.min(typeScore, categoryScore + 4);

        return Number.isFinite(score) ? [{ category, score, type }] : [];
      });
    })
    .sort((left, right) => {
      if (left.score !== right.score) return left.score - right.score;

      const categoryComparison = left.category.name.localeCompare(
        right.category.name,
        'es'
      );
      if (categoryComparison !== 0) return categoryComparison;
      return left.type.name.localeCompare(right.type.name, 'es');
    })
    .map(({ category, type }) => ({ category, type }));
}

export function createBusinessClassificationSelection(
  category: BusinessCategoryOption,
  type: BusinessTypeOption,
  otherBusinessTypeText = ''
): BusinessClassificationSelection {
  return {
    categoryId: category.id,
    categoryName: category.name,
    categorySlug: category.slug,
    otherBusinessTypeText: type.requiresCustomText
      ? otherBusinessTypeText.slice(0, MAX_OTHER_BUSINESS_TYPE_LENGTH)
      : '',
    requiresCustomText: type.requiresCustomText,
    typeId: type.id,
    typeName: type.name,
    typeSlug: type.slug,
  };
}

export function isBusinessClassificationComplete(
  selection: BusinessClassificationSelection | null
): boolean {
  if (!selection?.categoryId || !selection.typeId) return false;
  return (
    !selection.requiresCustomText ||
    selection.otherBusinessTypeText.trim().length >= 2
  );
}

export function getBusinessTypeDisplayName(
  selection:
    | BusinessClassificationSelection
    | BusinessClassificationAssignment
): string {
  if (selection.requiresCustomText) {
    const customText = selection.otherBusinessTypeText.trim();
    if (customText) return customText;
  }

  return selection.typeName;
}

export function resolveBusinessClassificationAssignments(
  categories: readonly BusinessCategoryOption[],
  assignments: readonly BusinessCategoryAssignmentRecord[]
): BusinessClassificationAssignment[] {
  const typeIndex = new Map<
    string,
    { category: BusinessCategoryOption; type: BusinessTypeOption }
  >();

  categories.forEach((category) => {
    category.types.forEach((type) => {
      typeIndex.set(type.id, { category, type });
    });
  });

  return assignments
    .flatMap((assignment) => {
      const match = typeIndex.get(assignment.typeId);
      if (!match) return [];

      return [
        {
          ...createBusinessClassificationSelection(
            match.category,
            match.type,
            assignment.otherBusinessTypeText ?? ''
          ),
          assignmentId: assignment.assignmentId,
          businessId: assignment.businessId,
          createdAt: assignment.createdAt,
          hiddenByPlan: assignment.hiddenByPlan,
          isActive: assignment.isActive,
          isPrimary: assignment.isPrimary,
        },
      ];
    })
    .sort((left, right) => {
      if (left.isPrimary !== right.isPrimary) return left.isPrimary ? -1 : 1;
      return left.createdAt.localeCompare(right.createdAt);
    });
}
